import { A11yModule, FocusOrigin } from '@angular/cdk/a11y'
import { isPlatformBrowser, UpperCasePipe } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  PLATFORM_ID,
  Signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { RouterLink } from '@angular/router'
import { filter, fromEvent, map, pairwise, throttleTime } from 'rxjs'

import { FOOTER_DATA } from 'app/core/cms/footer-data.token'
import { HEADER_DATA } from 'app/core/cms/header-data.token'
import { CURRENT_LANG } from 'app/core/i18n/language.token'
import { MetaService } from 'app/core/meta/meta.service'
import { SvgComponent } from 'app/shared/svg/svg.component'
import { checkEventTargetFn } from 'app/utils/check-event-target.function'
import { extendLangItems, ExtLangItem } from 'app/utils/extend-lang-items.function'

import { CmsIdDirective } from '../cms-id.directive'
import { CmsLinkDirective } from '../cms-link/cms-link.directive'
import { CmsLinkActiveDirective } from '../cms-link/cms-link-active.directive'
import { RippleDirective } from '../ripple/ripple.directive'
import { IconComponent } from '../svg/icon.component'
import { SwitchingChevronComponent } from '../switching-chevron/switching-chevron.component'
import { TranslatePipe } from '../translate.pipe'
import { SLIDE_DOWN_TRIGGER, SLIDE_LEFT_TRIGGER } from './header.animations'
import { SideNavService } from './side-nav/side-nav.service'

@Component({
  selector: 'pai-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UpperCasePipe,
    RouterLink,
    SvgComponent,
    TranslatePipe,
    RippleDirective,
    SwitchingChevronComponent,
    A11yModule,
    IconComponent,
    CmsIdDirective,
    CmsLinkDirective,
    CmsLinkActiveDirective,
  ],
  animations: [SLIDE_DOWN_TRIGGER, SLIDE_LEFT_TRIGGER],
  host: {
    '[attr.role]': '"banner"',
    '(window:resize)': 'onResize()',
  },
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  readonly sideNav = inject(SideNavService)

  protected readonly header = inject(HEADER_DATA)
  protected readonly footer = inject(FOOTER_DATA)
  protected readonly languages: Signal<ExtLangItem[] | null>
  protected readonly currentLanguage = inject(CURRENT_LANG)

  protected readonly logoAspectRatio = `${this.header.logo?.width ?? 1} / ${this.header.logo?.height ?? 1}`

  private readonly element: HTMLElement = inject(ElementRef).nativeElement
  private readonly injector = inject(Injector)
  private readonly cdRef = inject(ChangeDetectorRef)
  private readonly isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID))
  private readonly subNavState = new Map<any, boolean>()
  private necessaryHeaderWidth?: number

  constructor() {
    const meta = inject(MetaService)

    this.languages = toSignal(
      meta.altUrls$.pipe(map(extendLangItems.bind(void 0, this.footer.languages, this.currentLanguage))),
      { initialValue: null },
    )
    if (this.isBrowser) {
      const doc = inject(DOCUMENT)

      fromEvent(doc, 'scroll')
        .pipe(
          throttleTime(200, undefined, { leading: true, trailing: true }),
          map(() => doc.documentElement.scrollTop),
          pairwise(),
        )
        .subscribe(this.whenScrolled)

      fromEvent<MouseEvent>(doc, 'click')
        .pipe(filter(checkEventTargetFn(this.element)))
        .subscribe(this.closeAllSubNavs.bind(this))
    }
  }

  ngAfterViewInit() {
    // todo use `afterNextRender` instead of ngAfterViewInit
    if (this.isBrowser) {
      setTimeout(this.checkMenuListVisibility)
    }
  }

  ngOnDestroy(): void {
    if (this.sideNav.isOpen()) {
      this.sideNav.close()
    }
  }

  protected onResize() {
    if (this.isBrowser) {
      this.checkMenuListVisibility()
    }
  }

  protected toggleSideNav() {
    void this.sideNav.toggle(this.injector)
  }

  protected handleFocusChange(ev: FocusOrigin, subNavId: any) {
    if (ev === null) {
      this.subNavState.set(subNavId, false)
    }
  }

  protected toggleSubNav(subNavId: any) {
    const currState = this.subNavState.get(subNavId)
    this.subNavState.clear()
    this.subNavState.set(subNavId, !currState)
  }

  protected closeAllSubNavs() {
    this.subNavState.clear()
    this.cdRef.markForCheck()
  }

  protected isOpen(item: any): boolean {
    return !!this.subNavState.get(item)
  }

  private readonly whenScrolled = ([prev, now]: [number, number]) => {
    const downScrolling = now > 72 && prev - now < 0
    this.element.classList.toggle('--hide-burger-nav', downScrolling)
  }

  private checkMenuListVisibility = () => {
    const el = this.element
    const hasOverflow = (this.necessaryHeaderWidth || el.scrollWidth) > el.offsetWidth
    if (!this.necessaryHeaderWidth && hasOverflow) {
      // we start with the 'desktop nav'
      //  when nav is overflowing (=not enough space) we store the scrollWidth
      //  -- but only the first time (when the desktop nav is visible; wrong val otherwise)
      this.necessaryHeaderWidth = el.scrollWidth
    }
    el.classList.toggle('--burger-nav', hasOverflow)
    el.classList.toggle('--normal-nav', !hasOverflow)
  }
}
